/**
 * Based on https://apitest.coop.se/recipe/swagger/index.html
 * Note: add more functions here if you need them
 */

import type {
    ApiCategoriesUrlOrder,
    ApiCategory,
    ApiFavouriteRecipe,
    ApiRecipe,
    ApiRecipeComment,
} from '../../../../models/recipe/recipeModel';
import { getAuthToken } from '../auth/getAuthToken';
import getDirectRecipeAxiosClient from './directRecipeAxiosClient';
import type { ApiSaveCommentRequest } from './requests/recipeApiRequests';
import type {
    ApiFilterRecipes,
    ApiNorthforkSearchRecipesByEanRequest,
    ApiNorthforkSearchResult,
    ApiRecipeCategory,
} from './responses/recipeApiReponses';
import recipeApiUtils from './utils';

const getRecipesById = async (recipeIds: (string | number)[]): Promise<ApiRecipe[]> => {
    if (recipeIds?.length > 0) {
        // API doesn't allow "?recipeIds[]=""
        const queryWithoutBrackets = recipeApiUtils.createArrayParamWithoutBrackets(
            'recipeIds',
            recipeIds,
        );
        const response = await getDirectRecipeAxiosClient(null).get(
            `recipes${queryWithoutBrackets}`,
        );
        return response.data;
    }
    return [];
};

const getRecipeById = async (recipeId: string | number): Promise<ApiRecipe> => {
    const response = await getDirectRecipeAxiosClient(null).get(`recipes/${recipeId}`);

    return response.data;
};

/** Note: annymous users are also able to rate recipes */
const rateRecipe =
    (token: string | null) =>
    async ({
        recipeId,
        rating1to5,
    }: {
        recipeId: string;
        rating1to5: number;
    }): Promise<200 | 400> => {
        const response = await getDirectRecipeAxiosClient(token).post(
            `/recipes/${recipeId}/rating`,
            {
                rating: rating1to5,
            },
        );

        return response.data;
    };

const getRecipeUserRatings =
    (token: string) =>
    async (recipeId: number): Promise<{ recipeId: number; rating: number }> => {
        const response = await getDirectRecipeAxiosClient(token).get(
            `/recipes/${recipeId}/rating/me`,
        );

        return response.data;
    };

const getFavouriteRecipes = (token: string) => async (): Promise<ApiFavouriteRecipe[]> => {
    const response = await getDirectRecipeAxiosClient(token).get('/favorites/me');

    return response.data;
};

const saveFavouriteRecipe =
    (token: string) =>
    async (recipeId: string): Promise<boolean> => {
        const response = await await getDirectRecipeAxiosClient(token).post<{
            addFavoriteRecipeResult: boolean;
        }>(`/recipes/${recipeId}/favorite`);

        return response?.data?.addFavoriteRecipeResult || false;
    };

const removeFavouriteRecipe =
    (token: string) =>
    async (recipeId: string): Promise<boolean> => {
        const response = await await getDirectRecipeAxiosClient(token).delete<{
            removeFavoriteRecipeResult: boolean;
        }>(`/recipes/${recipeId}/favorite`);

        return response?.data?.removeFavoriteRecipeResult || false;
    };

const getRecipeSingleMetaDataByCategoriesBatch = async (
    data: Record<string, { category1: string; category2?: string }>,
) => {
    const client = getDirectRecipeAxiosClient(null);
    const response = await client.post('/categories/meta-data/batch', data);
    return response.data;
};

const getCategoriesUrlOrderData = async (): Promise<ApiCategoriesUrlOrder> => {
    const client = getDirectRecipeAxiosClient(null);
    const response = await client.get('/categories/url-order');

    return response.data;
};

const getCategoriesUrlSegments = async (): Promise<ApiCategory[]> => {
    const client = getDirectRecipeAxiosClient(null);
    const response = await client.get('/categories/');

    return response.data;
};

const getNorthforkSearchRecipeByEan = async (
    body: ApiNorthforkSearchRecipesByEanRequest,
): Promise<ApiNorthforkSearchResult> => {
    const { token } = await getAuthToken();
    const client = await getDirectRecipeAxiosClient(token);
    const response = await client.post('/northfork/search/ean', body);

    return response.data;
};

const getRecipeComments = async (recipeExternalId: string): Promise<ApiRecipeComment[]> => {
    const response = await getDirectRecipeAxiosClient(null).get(
        `/recipes/${recipeExternalId}/comments`,
    );
    return response.data;
};

const saveRecipeComment =
    (token: string) =>
    async (request: ApiSaveCommentRequest): Promise<void> => {
        const response = await getDirectRecipeAxiosClient(token).post(
            `/recipes/${request.recipeExternalId}/comment`,
            { name: request.authorName, comment: request.comment },
        );
        return response.data;
    };

const getRecipeCategoriesById = async (recipeExternalId: string): Promise<ApiRecipeCategory[]> => {
    const response = await getDirectRecipeAxiosClient(null).get(
        `/recipes/${recipeExternalId}/categories`,
    );
    return response.data;
};

const getRecipeCategories = async (): Promise<ApiRecipeCategory[]> => {
    const response = await getDirectRecipeAxiosClient(null).get(`/categories`);
    return response.data;
};

const filterRecipesAdminTool = async (
    token: string,
    filterString: string,
    excludeList: string[],
): Promise<ApiFilterRecipes> => {
    const response = await getDirectRecipeAxiosClient(token).post(`/recipes/filter`, {
        filter: filterString,
        exclude: excludeList,
    });

    return response.data;
};

export default {
    getRecipeById,
    getRecipesById,
    rateRecipe,
    getRecipeUserRatings,
    getRecipeComments,
    saveRecipeComment,
    getFavouriteRecipes,
    saveFavouriteRecipe,
    removeFavouriteRecipe,
    getRecipeSingleMetaDataByCategoriesBatch,
    getCategoriesUrlOrderData,
    getCategoriesUrlSegments,
    getRecipeCategoriesById,
    getRecipeCategories,
    northfork: {
        getRecipesByEans: getNorthforkSearchRecipeByEan,
    },
    filterRecipesAdminTool,
};
